import {
  BaSeButton,
  BaSeContainer,
  BaSeDataTable,
  BaSeDialogContext,
  BaSeParagraph,
  BaSeShapeButton,
  BaSeTheme,
} from '@base/react';
import * as Sebrae from '@sebrae/react-core';
import * as React from 'react';
import { Role } from '../../models/role';
import {
  BackWrapper,
  ButtonsGrid,
  DataTableWrapper,
  PaddingContainer,
  Return,
  StyledJumbotron,
} from '../../components/shared/grid/grid-wrapper-styled';
import EadTitlePage from '../../components/layout/ead-title-page';
import { GestaoArquivos } from '../../components/gestao-arquivos/gestao-arquivos';
import { EnviarArquivos } from '../../components/gestao-arquivos/enviar-arquivos';
import TemplateCertificados from '../../components/template-certificados/template-certificados';
import { CERTIFICADO_TEMPLATE, actionRequest } from '../../hooks/constants';
import { TemplateDefault } from '../../components/template-certificados/template-default';

// eslint-disable-next-line sonarjs/cognitive-complexity
const TemplatesCertificadosPage = () => {
  const [isFormTemplate, setIsFormTemplate] = React.useState(false);
  const [isFileMenagement, setIsFileMenagement] = React.useState(false);
  const [titleFormTemplate, setTitleFormTemplate] = React.useState('');
  const [isSendFile, setIsSendFile] = React.useState(false);
  const [templateProps, setTemplateProps] = React.useState('');
  const [typeAction, setTypeAction] = React.useState('');
  const [listTemplates, setListTemplates] = React.useState([]);

  const { showNotification, showModal } = React.useContext(BaSeDialogContext);
  const { backendPost, backendPut, backendGet, backendDelete } = Sebrae.useBackend();
  const { fetchData, data, loading } = Sebrae.usePagination(
    `${CERTIFICADO_TEMPLATE}/grid`,
  );
  const { update, show, newFromThis } = actionRequest;
  const { hasRoles } = Sebrae.useAuth();

  const { white } = BaSeTheme.colors.defaultColors;
  const { azulSebrae36, cinzaSebrae45 } = BaSeTheme.colors.institucionais;

  const returnToGrid = React.useCallback(() => {
    setIsFormTemplate(false);
    setIsFileMenagement(false);
  }, []);

  const handleActionButtons = React.useCallback(
    (isTemplate, isMenagementFiles, nomTitle, action = '') => {
      setIsFormTemplate(isTemplate);
      setIsFileMenagement(isMenagementFiles);
      setTitleFormTemplate(nomTitle);
      setTypeAction(action);
    },
    [],
  );

  const actionTemplate = React.useCallback(
    (template, action) => {
      const textAction =
        action === update ? 'Editar' : action === show ? 'Visualizar' : 'Novo';
      const subtitle =
        `${textAction} template ` +
        (action !== newFromThis ? ` - ${template.nome}` : '');
      handleActionButtons(true, false, subtitle, actionRequest[action]);
      setTemplateProps(template);
    },
    [handleActionButtons, newFromThis, show, update],
  );

  const dateDispatch = React.useCallback((values) => {
    const valueDate = new Date(values?.dataAtualizacao);
    const date = new Intl.DateTimeFormat().format(valueDate);
    const hour = new Intl.DateTimeFormat('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(valueDate);
    return `${date} - ${hour.replace(/[:]+/g, 'h')}`;
  }, []);

  const handleListDefault = React.useCallback(() => {
    backendGet(`${CERTIFICADO_TEMPLATE}/lista`)
      .then((data) => {
        const templates = data?.map((item) => ({
          id: item.id,
          label: item.descricao,
        }));
        setListTemplates(templates);
      })
      .catch((error) =>
        showNotification({
          message:
            'Não foi possível recuperar a lista de templates de certificado!',
          color: 'destructive',
        }),
      )
      .finally();
  }, [backendGet, showNotification]);

  const handleSave = React.useCallback(
    async (formData) => {
      try {
        await backendPost(`${CERTIFICADO_TEMPLATE}`, formData);
        fetchData({ sort: { field: 'dataAtualizacao', type: 'DESC' } });
        handleListDefault();
        return { sucess: true };
      } catch (e) {
        console.log(e);
        return { ...e };
      }
    },
    [backendPost, fetchData, handleListDefault],
  );

  const handleUpdate = React.useCallback(
    async (formData) => {
      try {
        await backendPut(
          `${CERTIFICADO_TEMPLATE}/${templateProps.id}`,
          formData,
        );
        fetchData({ sort: { field: 'dataAtualizacao', type: 'DESC' } });
        handleListDefault();
        return { sucess: true };
      } catch (e) {
        console.log(e);
        return { ...e };
      }
    },
    [backendPut, fetchData, templateProps, handleListDefault],
  );

  const handleSaveTemplateDefault = React.useCallback(
    async (id) => {
      try {
        await backendPost(`${CERTIFICADO_TEMPLATE}/${id}/padrao`);
        handleListDefault();
        return { sucess: true };
      } catch (e) {
        console.log(e);
        return { ...e };
      }
    },
    [backendPost, handleListDefault],
  );

  const handleDeleteWithBody = React.useCallback(
    async (id, dtoForm) => {
      try {
        await backendPost(`${CERTIFICADO_TEMPLATE}/${id}/deleta`, dtoForm);
        fetchData({ sort: { field: 'dataAtualizacao', type: 'DESC' } });
        handleListDefault();
        return { sucess: true };
      } catch (e) {
        console.log(e);
        return { ...e };
      }
    },
    [backendPost, fetchData, handleListDefault],
  );

  const handleDelete = React.useCallback(
    async (id) => {
      try {
        await backendDelete(`${CERTIFICADO_TEMPLATE}/${id}`);
        fetchData({ sort: { field: 'dataAtualizacao', type: 'DESC' } });
        handleListDefault();
        return { sucess: true };
      } catch (e) {
        console.log(e);
        return { ...e };
      }
    },
    [backendDelete, fetchData, handleListDefault],
  );

  const modalConfigs = React.useCallback(
    (
      message,
      func,
      title,
      actions,
      Content,
      size = 'large',
      isFluid,
      breakpointDelimiter,
    ) => ({
      size,
      title,
      actions,
      isFluid,
      breakpointDelimiter,
      beforeClose: (action) => func?.(action, message),
      content: (onClose) => React.cloneElement(Content, { onClose }),
    }),
    [],
  );

  const handleModalAction = React.useCallback(
    (isDelete, value = null) => {
      const titleModal = isDelete ? 'Atenção' : 'Definir novo padrão';

      return modalConfigs(
        null,
        null,
        titleModal,
        [],
        <TemplateDefault
          openModal={showModal}
          notify={showNotification}
          handleConfirm={
            !!value ? handleDeleteWithBody : handleSaveTemplateDefault
          }
          handleDelete={handleDelete}
          isDelete={isDelete}
          idTemplate={value?.id ?? ''}
          listTemplates={listTemplates}
        />,
        'medium',
        true,
        'mobileLarge',
      );
    },
    [
      handleDelete,
      handleDeleteWithBody,
      handleSaveTemplateDefault,
      listTemplates,
      modalConfigs,
      showModal,
      showNotification,
    ],
  );

  const handleActions = React.useCallback(
    (values) => {
      return (
        <>
          <BaSeShapeButton
            buttonType="button"
            color={cinzaSebrae45}
            nameIcon="eye"
            onClick={() => actionTemplate(values, show)}
            shape="circle"
            size="medium"
            sizeIcon="medium"
            type="tertiary"
          />
          <BaSeShapeButton
            buttonType="button"
            color={cinzaSebrae45}
            nameIcon="copy"
            onClick={() => actionTemplate(values, newFromThis)}
            shape="circle"
            size="medium"
            sizeIcon="medium"
            type="tertiary"
          />
          <BaSeShapeButton
            buttonType="button"
            color={cinzaSebrae45}
            nameIcon="pen"
            onClick={() => actionTemplate(values, update)}
            shape="circle"
            size="medium"
            sizeIcon="medium"
            type="tertiary"
          />
          <BaSeShapeButton
            buttonType="button"
            color={cinzaSebrae45}
            nameIcon="trash-alt"
            onClick={() => showModal(handleModalAction(true, values))}
            shape="circle"
            size="medium"
            sizeIcon="medium"
            type="tertiary"
          />
        </>
      );
    },
    [
      actionTemplate,
      cinzaSebrae45,
      handleModalAction,
      newFromThis,
      show,
      showModal,
      update,
    ],
  );

  React.useEffect(() => {
    handleListDefault();
  }, [handleListDefault]);

  return (
    <div style={{ background: white }}>
      <StyledJumbotron backgroundColor={white}>
        <BaSeContainer delimitedOnBreakpoint="desktop">
          {(isFormTemplate || isFileMenagement) && (
            <div style={{ marginTop: 16 }}>
              <BackWrapper onClick={returnToGrid}>
                <Return />
                <BaSeParagraph color={azulSebrae36}>Voltar</BaSeParagraph>
              </BackWrapper>
            </div>
          )}
          <EadTitlePage
            title={
              isFileMenagement || isSendFile
                ? 'Gestão de arquivos'
                : 'Templates de certificados'
            }
            subtitle={
              isFormTemplate || isFileMenagement || isSendFile
                ? titleFormTemplate
                : 'Gestão'
            }
          />
          {hasRoles([Role.Administrador]) &&
            !isFormTemplate &&
            !isFileMenagement &&
            !isSendFile && (
              <ButtonsGrid>
                <BaSeButton
                  value="Definir padrão"
                  leftIcon="star"
                  type="secondary"
                  color={azulSebrae36}
                  onClick={() => showModal(handleModalAction(false))}
                />
                <BaSeButton
                  value="Gerenciar arquivos"
                  leftIcon="file"
                  type="secondary"
                  color={azulSebrae36}
                  onClick={() =>
                    handleActionButtons(false, true, 'Certificados')
                  }
                />
                <BaSeButton
                  value="Criar template"
                  onClick={() =>
                    handleActionButtons(true, false, 'Novo template', 'create')
                  }
                  leftIcon="plus"
                />
              </ButtonsGrid>
            )}
          {isFileMenagement && !isSendFile && (
            <ButtonsGrid>
              <BaSeButton
                value="Enviar arquivos"
                type="secondary"
                color={azulSebrae36}
                onClick={() => {
                  setIsFileMenagement(false);
                  setIsSendFile(true);
                  setTitleFormTemplate('Certificados');
                }}
              />
            </ButtonsGrid>
          )}
        </BaSeContainer>
      </StyledJumbotron>
      <BaSeContainer delimitedOnBreakpoint="desktop">
        <PaddingContainer>
          {isFileMenagement && (
            <GestaoArquivos
              codigoProduto={null}
              isTemplateCertificados={true}
            />
          )}
          {isFormTemplate && (
            <TemplateCertificados
              setIsFormTemplate={setIsFormTemplate}
              save={handleSave}
              update={handleUpdate}
              notify={showNotification}
              setIsUpdate={'setIsUpdate'}
              typeAction={typeAction}
              returnToGrid={returnToGrid}
              idTemplate={templateProps.id}
            />
          )}
          {isSendFile && (
            <EnviarArquivos
              setIsSendFile={setIsSendFile}
              setIsFileMenage={setIsFileMenagement}
              codigoProduto={'codigoProduto'}
              isTemplateCertificados={true}
            />
          )}
          {!isFileMenagement && !isFormTemplate && !isSendFile && (
            <DataTableWrapper>
              <BaSeDataTable
                backgroundColor={white}
                columnConfig={[
                  {
                    columnId: 'dataAtualizacao',
                    width: 200,
                  },
                  {
                    columnId: 'actions',
                    width: 250,
                  },
                ]}
                config={[
                  {
                    accessorKey: 'nome',
                    header: 'Template',
                  },
                  {
                    accessorKey: 'dataAtualizacao',
                    header: 'Última alteração',
                    mapper: ({ values }) => dateDispatch(values),
                  },
                  {
                    accessorKey: 'criadoPor',
                    header: 'Usuário cadastrante',
                  },
                  {
                    accessorKey: 'actions',
                    header: 'Ações',
                    mapper: ({ values }) =>
                      hasRoles([Role.Administrador])
                        ? handleActions(values)
                        : '',
                  },
                ]}
                fetchData={fetchData}
                title="Relação de templates"
                hasPagination
                hasSorting
                hasTextFilter
                onTextFilter={({ setFilter, term }) => {
                  setFilter({ filter: term });
                }}
                textFilterAlwaysVisible
                initialPageIndex={0}
                initialPageSize={10}
                initialSortField="dataAtualizacao"
                initialSortType="DESC"
                isLoading={loading}
                items={data.items}
                total={data.total}
                genderOfItemDescription="male"
                singularItemDescription="template"
                pluralItemDescription="templates"
                itemsPerPage={[10, 20, 50]}
              />
            </DataTableWrapper>
          )}
        </PaddingContainer>
      </BaSeContainer>
    </div>
  );
};

export { TemplatesCertificadosPage as default };
